import { NonTimezoneSensitiveDate } from '@commenda/utils';

/**
 * Pre-compiled regex patterns for parsing month names in date strings.
 * These are hardcoded at module level to avoid ReDoS vulnerabilities.
 * Pattern: "March 1, 2025" or "Mar 1, 2025" or "mar, 1 2025"
 */
const MONTH_DATE_PATTERNS: Array<{
  pattern: RegExp;
  month: number;
}> = [
  // Full month names with comma
  { pattern: /^january\s+(\d{1,2}),?\s+(\d{4})$/i, month: 1 },
  { pattern: /^february\s+(\d{1,2}),?\s+(\d{4})$/i, month: 2 },
  { pattern: /^march\s+(\d{1,2}),?\s+(\d{4})$/i, month: 3 },
  { pattern: /^april\s+(\d{1,2}),?\s+(\d{4})$/i, month: 4 },
  { pattern: /^may\s+(\d{1,2}),?\s+(\d{4})$/i, month: 5 },
  { pattern: /^june\s+(\d{1,2}),?\s+(\d{4})$/i, month: 6 },
  { pattern: /^july\s+(\d{1,2}),?\s+(\d{4})$/i, month: 7 },
  { pattern: /^august\s+(\d{1,2}),?\s+(\d{4})$/i, month: 8 },
  { pattern: /^september\s+(\d{1,2}),?\s+(\d{4})$/i, month: 9 },
  { pattern: /^october\s+(\d{1,2}),?\s+(\d{4})$/i, month: 10 },
  { pattern: /^november\s+(\d{1,2}),?\s+(\d{4})$/i, month: 11 },
  { pattern: /^december\s+(\d{1,2}),?\s+(\d{4})$/i, month: 12 },
  // Abbreviated month names with comma
  { pattern: /^jan\s+(\d{1,2}),?\s+(\d{4})$/i, month: 1 },
  { pattern: /^feb\s+(\d{1,2}),?\s+(\d{4})$/i, month: 2 },
  { pattern: /^mar\s+(\d{1,2}),?\s+(\d{4})$/i, month: 3 },
  { pattern: /^apr\s+(\d{1,2}),?\s+(\d{4})$/i, month: 4 },
  { pattern: /^may\s+(\d{1,2}),?\s+(\d{4})$/i, month: 5 },
  { pattern: /^jun\s+(\d{1,2}),?\s+(\d{4})$/i, month: 6 },
  { pattern: /^jul\s+(\d{1,2}),?\s+(\d{4})$/i, month: 7 },
  { pattern: /^aug\s+(\d{1,2}),?\s+(\d{4})$/i, month: 8 },
  { pattern: /^sep\s+(\d{1,2}),?\s+(\d{4})$/i, month: 9 },
  { pattern: /^oct\s+(\d{1,2}),?\s+(\d{4})$/i, month: 10 },
  { pattern: /^nov\s+(\d{1,2}),?\s+(\d{4})$/i, month: 11 },
  { pattern: /^dec\s+(\d{1,2}),?\s+(\d{4})$/i, month: 12 },
  // Full month names without comma
  { pattern: /^january\s+(\d{1,2})\s+(\d{4})$/i, month: 1 },
  { pattern: /^february\s+(\d{1,2})\s+(\d{4})$/i, month: 2 },
  { pattern: /^march\s+(\d{1,2})\s+(\d{4})$/i, month: 3 },
  { pattern: /^april\s+(\d{1,2})\s+(\d{4})$/i, month: 4 },
  { pattern: /^may\s+(\d{1,2})\s+(\d{4})$/i, month: 5 },
  { pattern: /^june\s+(\d{1,2})\s+(\d{4})$/i, month: 6 },
  { pattern: /^july\s+(\d{1,2})\s+(\d{4})$/i, month: 7 },
  { pattern: /^august\s+(\d{1,2})\s+(\d{4})$/i, month: 8 },
  { pattern: /^september\s+(\d{1,2})\s+(\d{4})$/i, month: 9 },
  { pattern: /^october\s+(\d{1,2})\s+(\d{4})$/i, month: 10 },
  { pattern: /^november\s+(\d{1,2})\s+(\d{4})$/i, month: 11 },
  { pattern: /^december\s+(\d{1,2})\s+(\d{4})$/i, month: 12 },
  // Abbreviated month names without comma
  { pattern: /^jan\s+(\d{1,2})\s+(\d{4})$/i, month: 1 },
  { pattern: /^feb\s+(\d{1,2})\s+(\d{4})$/i, month: 2 },
  { pattern: /^mar\s+(\d{1,2})\s+(\d{4})$/i, month: 3 },
  { pattern: /^apr\s+(\d{1,2})\s+(\d{4})$/i, month: 4 },
  { pattern: /^may\s+(\d{1,2})\s+(\d{4})$/i, month: 5 },
  { pattern: /^jun\s+(\d{1,2})\s+(\d{4})$/i, month: 6 },
  { pattern: /^jul\s+(\d{1,2})\s+(\d{4})$/i, month: 7 },
  { pattern: /^aug\s+(\d{1,2})\s+(\d{4})$/i, month: 8 },
  { pattern: /^sep\s+(\d{1,2})\s+(\d{4})$/i, month: 9 },
  { pattern: /^oct\s+(\d{1,2})\s+(\d{4})$/i, month: 10 },
  { pattern: /^nov\s+(\d{1,2})\s+(\d{4})$/i, month: 11 },
  { pattern: /^dec\s+(\d{1,2})\s+(\d{4})$/i, month: 12 },
];

/**
 * Calculates a date range based on a predefined period code
 */
export const calculateDateRange = (
  period: string
): {
  from_date: string;
  to_date: string;
} => {
  let endDate = NonTimezoneSensitiveDate.fromNativeDate(new Date());
  let startDate = NonTimezoneSensitiveDate.fromNativeDate(new Date());

  switch (period) {
    case 'mtd':
      // Start of current month
      startDate = endDate.startOf('month');
      break;
    case 'pm':
      // Start of previous month
      startDate = endDate.subtract(1, 'month').startOf('month');
      // End of previous month
      endDate = endDate.subtract(1, 'month').endOf('month');
      break;
    case 'ytd':
      // Start of current year
      startDate = endDate.startOf('year');
      break;
    default: {
      const days = parseInt(period, 10);
      if (!Number.isNaN(days)) {
        startDate = endDate.subtract(days, 'days');
      }
      break;
    }
  }

  return {
    from_date: startDate.toISO8601String(),
    to_date: endDate.toISO8601String(),
  };
};

/**
 * Parses flexible date string formats like "Mar 1, 2025", "mar, 1 2025", "March 1, 2025", etc.
 * Returns ISO string if valid, null otherwise
 */
export const parseFlexibleDate = (dateString: string): string | null => {
  if (!dateString || !dateString.trim()) {
    return null;
  }

  const trimmed = dateString.trim();

  // Try MM/DD/YYYY format first (most common for this component)
  const mmddyyyyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmddyyyyMatch) {
    const month = parseInt(mmddyyyyMatch[1], 10);
    const day = parseInt(mmddyyyyMatch[2], 10);
    const year = parseInt(mmddyyyyMatch[3], 10);
    if (
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= 31 &&
      year >= 1000 &&
      year <= 9999
    ) {
      try {
        const ntsDate = new NonTimezoneSensitiveDate(year, month, day);
        return ntsDate.toISO8601String();
      } catch {
        return null;
      }
    }
  }

  // Try ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    try {
      const date = NonTimezoneSensitiveDate.fromISO8601String(trimmed);
      return date.toISO8601String();
    } catch {
      return null;
    }
  }

  // Try native Date parsing for flexible formats
  // This handles formats like "Mar 1, 2025", "March 1, 2025", "mar, 1 2025", etc.
  try {
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      const ntsDate = NonTimezoneSensitiveDate.fromNativeDate(parsed);
      return ntsDate.toISO8601String();
    }
  } catch {
    // Continue to other parsing methods
  }

  // Try to extract month, day, year using pre-compiled patterns
  // Patterns are defined at module level to avoid ReDoS vulnerabilities
  let parsedDate: string | null = null;

  for (let i = 0; i < MONTH_DATE_PATTERNS.length; i += 1) {
    const { pattern, month } = MONTH_DATE_PATTERNS[i];
    if (parsedDate) break;

    const match = trimmed.match(pattern);
    if (match) {
      const day = parseInt(match[1], 10);
      const year = parseInt(match[2], 10);

      if (day >= 1 && day <= 31 && year >= 1000 && year <= 9999) {
        try {
          const ntsDate = new NonTimezoneSensitiveDate(year, month, day);
          parsedDate = ntsDate.toISO8601String();
          break; // Found valid date, stop searching
        } catch {
          // Invalid date (e.g., Feb 30) - continue to next pattern
        }
      }
    }
  }

  if (parsedDate) return parsedDate;

  return null;
};

/**
 * Formats a date ISO string to "MM/DD/YYYY" format
 */
export const formatDateDisplay = (dateISO: string | undefined): string => {
  if (!dateISO) return '';
  try {
    const date = NonTimezoneSensitiveDate.fromISO8601String(dateISO);
    const month = date.getMonth();
    const day = date.getDate();
    const year = date.getYear();
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
  } catch {
    return '';
  }
};

/**
 * Auto-formats input string to MM/DD/YYYY format as user types
 * Adds slashes automatically and limits input length
 */
export const autoFormatDateInput = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Limit to 8 digits (MMDDYYYY)
  const limited = digits.slice(0, 8);

  // Add slashes at appropriate positions
  if (limited.length <= 2) {
    return limited;
  }
  if (limited.length <= 4) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  }
  return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
};
